/**
 * Life4Earth V2 — Hook for the list of Mini-Projects the current user
 * has read access to.
 *
 * Backend endpoint (/api/mini-projects) already filters via
 * CanReadMiniProject + the visibility model (migration 165), so the
 * caller doesn't need to apply any client-side permission filtering —
 * the array is pre-trimmed to what the user is allowed to see.
 *
 * Primary consumer: GlobalSearch in the application header (so mini
 * projects show up in the spotlight-style search dropdown). Other
 * consumers can use this same hook — there's no per-call cost beyond
 * the initial fetch since the result is stable across renders.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchMiniProjects,
  EVT_MINI_PROJECT_MUTATED,
  type MiniProject,
} from "./miniProjects";

export interface UseApiMiniProjectsResult {
  miniProjects: MiniProject[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useApiMiniProjects(): UseApiMiniProjectsResult {
  const mountedRef = useRef(true);
  const [state, setState] = useState<{
    miniProjects: MiniProject[];
    loading: boolean;
    error: string | null;
  }>({
    miniProjects: [],
    loading: true,
    error: null,
  });

  const load = useCallback(async () => {
    if (!mountedRef.current) return;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      // No filters — server returns everything the caller can read,
      // which is exactly what GlobalSearch wants to search across.
      const result = await fetchMiniProjects({});
      if (!mountedRef.current) return;
      setState({ miniProjects: result, loading: false, error: null });
    } catch (err) {
      if (!mountedRef.current) return;
      setState({
        miniProjects: [],
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => { mountedRef.current = false; };
  }, [load]);

  // Task #221. MP mutations elsewhere in the app dispatch
  // EVT_MINI_PROJECT_MUTATED. The MP list itself can change too —
  // lifecycle transitions, archive/unarchive, title or owner edits —
  // so the portfolio aggregator (App.tsx) needs fresh rows. Listener
  // is global; the bulk MP list fetch is a single round-trip.
  //
  // 2026-05-27 cross-browser fix — also listen for
  // `l4e_project_updated`, the window event the SignalR connection
  // fires on receipt of a KpiHub `ProjectUpdated` push.
  // MiniProjectStatusesController now broadcasts on every status
  // create/update/quick-update so a second user on a different browser
  // sees the MP list refresh without manual reload.
  useEffect(() => {
    const onMutated = () => { load(); };
    if (typeof window !== "undefined") {
      window.addEventListener(EVT_MINI_PROJECT_MUTATED, onMutated);
      window.addEventListener("l4e_project_updated", onMutated);
      return () => {
        window.removeEventListener(EVT_MINI_PROJECT_MUTATED, onMutated);
        window.removeEventListener("l4e_project_updated", onMutated);
      };
    }
    return undefined;
  }, [load]);

  return { ...state, refetch: load };
}
